import type {
  EthereumTransaction,
  DecodedTransaction,
  DecodedField,
} from '@txviz/core';
import {
  formatValue,
  formatGasPrice,
  sanitizeDisplayString,
  bytesToHex,
} from '@txviz/core';
import { decodeCalldata } from './abi-lookup.js';
import { serializeEthereumTransaction } from './serializer.js';
import { KNOWN_SELECTORS } from './types.js';

/**
 * Decode an Ethereum transaction into human-readable fields.
 */
export async function decodeEthereumTransaction(
  tx: EthereumTransaction,
  abi?: readonly unknown[],
): Promise<DecodedTransaction> {
  const fields: DecodedField[] = [];
  const serialized = serializeEthereumTransaction(tx);
  const rawHex = bytesToHex(serialized);

  // Transaction type
  const txTypeLabel = getTxTypeLabel(tx.type);
  fields.push({
    label: 'Type',
    value: txTypeLabel,
    rawValue: tx.type,
    type: 'text',
  });

  // Chain ID
  fields.push({
    label: 'Chain ID',
    value: getChainName(tx.chainId),
    rawValue: tx.chainId.toString(),
    type: 'number',
  });

  // Nonce
  fields.push({
    label: 'Nonce',
    value: tx.nonce.toString(),
    rawValue: tx.nonce.toString(),
    type: 'number',
  });

  // To address
  if (tx.to !== null) {
    fields.push({
      label: 'To',
      value: tx.to,
      rawValue: tx.to,
      type: 'address',
    });
  } else {
    fields.push({
      label: 'To',
      value: 'Contract Deployment',
      rawValue: 'null',
      type: 'text',
      warning: 'This transaction deploys a new contract',
    });
  }

  // Value
  fields.push({
    label: 'Value',
    value: formatValue(tx.value, 'ethereum'),
    rawValue: tx.value.toString(),
    type: 'value',
  });

  // Gas fields (type-specific)
  fields.push({
    label: 'Gas Limit',
    value: tx.gasLimit.toString(),
    rawValue: tx.gasLimit.toString(),
    type: 'number',
  });

  if (tx.type === 'legacy' || tx.type === 'eip2930') {
    fields.push({
      label: 'Gas Price',
      value: formatGasPrice(tx.gasPrice),
      rawValue: tx.gasPrice.toString(),
      type: 'value',
    });
  }

  if (tx.type === 'eip1559' || tx.type === 'eip4844') {
    fields.push({
      label: 'Max Fee Per Gas',
      value: formatGasPrice(tx.maxFeePerGas),
      rawValue: tx.maxFeePerGas.toString(),
      type: 'value',
    });
    fields.push({
      label: 'Max Priority Fee',
      value: formatGasPrice(tx.maxPriorityFeePerGas),
      rawValue: tx.maxPriorityFeePerGas.toString(),
      type: 'value',
    });
  }

  if (tx.type === 'eip4844') {
    fields.push({
      label: 'Max Fee Per Blob Gas',
      value: formatGasPrice(tx.maxFeePerBlobGas),
      rawValue: tx.maxFeePerBlobGas.toString(),
      type: 'value',
    });
    fields.push({
      label: 'Blob Hashes',
      value: `${tx.blobVersionedHashes.length} blob(s)`,
      rawValue: JSON.stringify(tx.blobVersionedHashes),
      type: 'data',
    });
  }

  // Access list (if present)
  if ('accessList' in tx && tx.accessList.length > 0) {
    fields.push({
      label: 'Access List',
      value: `${tx.accessList.length} address(es)`,
      rawValue: JSON.stringify(tx.accessList),
      type: 'data',
    });
  }

  // Calldata decoding
  if (tx.data && tx.data !== '0x' && tx.data.length >= 10) {
    const decoded = await decodeCalldata(tx.data, abi);
    if (decoded) {
      fields.push(...decoded.fields);
    } else {
      fields.push({
        label: 'Calldata',
        value: tx.data.length > 74 ? `${tx.data.slice(0, 74)}... (${(tx.data.length - 2) / 2} bytes)` : tx.data,
        rawValue: tx.data,
        type: 'data',
        warning: 'Unable to decode calldata — function signature unknown',
      });
    }
  }

  // Build summary
  const summary = buildSummary(tx);

  return {
    chain: 'ethereum',
    type: txTypeLabel,
    summary,
    fields,
    rawHex,
  };
}

function getTxTypeLabel(type: string): string {
  switch (type) {
    case 'legacy': return 'Legacy Transaction';
    case 'eip2930': return 'EIP-2930 (Access List)';
    case 'eip1559': return 'EIP-1559 (Fee Market)';
    case 'eip4844': return 'EIP-4844 (Blob)';
    default: return `Unknown Type (${type})`;
  }
}

function getChainName(chainId: bigint): string {
  switch (chainId) {
    case 1n: return 'Ethereum Mainnet (1)';
    case 5n: return 'Goerli (5)';
    case 11155111n: return 'Sepolia (11155111)';
    case 137n: return 'Polygon (137)';
    case 42161n: return 'Arbitrum One (42161)';
    case 10n: return 'Optimism (10)';
    case 8453n: return 'Base (8453)';
    case 56n: return 'BNB Chain (56)';
    case 43114n: return 'Avalanche C-Chain (43114)';
    default: return `Chain ${chainId.toString()}`;
  }
}

function buildSummary(tx: EthereumTransaction): string {
  const parts: string[] = [];

  if (tx.to === null) {
    parts.push('Deploy contract');
  } else if (tx.data === '0x' || tx.data.length < 10) {
    // Simple transfer
    parts.push(`Transfer ${formatValue(tx.value, 'ethereum')} to ${tx.to}`);
  } else {
    // Contract call
    const selector = tx.data.slice(0, 10).toLowerCase();
    const known = KNOWN_SELECTORS[selector];
    if (known) {
      parts.push(`${known.standard} ${known.name}`);
      if (tx.to) parts.push(`on ${tx.to}`);
    } else {
      parts.push(`Call contract ${tx.to ?? '0x'}`);
    }
    if (tx.value > 0n) {
      parts.push(`with ${formatValue(tx.value, 'ethereum')}`);
    }
  }

  return sanitizeDisplayString(parts.join(' '));
}
